import { useTranslation } from 'react-i18next';
import { Grid3x3GapFill } from 'react-bootstrap-icons';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import LockIcon from '@mui/icons-material/Lock';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@mui/styles/styled';
import { renderablePropType } from '../../../../utils/propTypes';
import { makeColumnTranslation } from '../../../../utils/table';
import DrawerButton from '../../../DrawerButton';
import DrawerBody from '../../../DrawerBody';
import DrawerHeader from '../../../DrawerHeader';
import ResetButton from '../../../DrawerButton/ResetButton';
import Section from '../../../DrawerButton/Section';

const OffsetCheckbox = styled(Checkbox)(({ theme }) => ({
	marginLeft: theme.spacing(-2),
}));

const LockIconButton = styled(IconButton)(({ theme }) => ({
	marginLeft: theme.spacing(-2.5),
}));

const DraggableListItem = styled(({ isModifiable, ...props }) => (
	<ListItem
		{...props}
		sx={{
			display: 'flex',
			justifyContent: 'space-between',
			cursor: isModifiable ? 'pointer' : 'default',
		}}
	/>
))({});

const DragIcon = styled(DragIndicatorIcon)(({ theme }) => ({
	color: theme.palette.grey[500],
	cursor: 'grab',
}));

function ColumnCustomizer({
	title,
	order,
	visibility,
	onReset,
	onOrderChange,
	onVisibilityChange,
	columnConfig,
	ButtonProps = {},
	dataTestId,
	...props
}) {
	const { t } = useTranslation('common');
	const [isOpen, setIsOpen] = useState(false);
	const columnT = makeColumnTranslation(t, 'orders:columns');
	const [hoveredCol, setHoveredCol] = useState(null);

	return (
		<DrawerButton
			startIcon={
				<Grid3x3GapFill
					role="graphics-symbol"
					aria-hidden="true"
					aria-label="Filter Circle"
					size={16}
				/>
			}
			buttonText={title || t('customize')}
			isOpen={isOpen}
			onOpen={() => setIsOpen(true)}
			onClose={() => setIsOpen(false)}
			ButtonProps={{
				...{ 'data-testid': dataTestId || 'table_column_customizer' },
				...ButtonProps,
			}}
			{...props}
		>
			<DrawerHeader onClose={() => setIsOpen(false)}>
				<ResetButton onClick={onReset} data-testid="reset_orders_table_columns">
					{t('reset_columns')}
				</ResetButton>
			</DrawerHeader>

			<DrawerBody>
				<Section>
					<List>
						{order.map((column, i) => {
							const isModifiable = !columnConfig[column].isPersisted;
							let dragProps = {};
							if (isModifiable) {
								dragProps = {
									draggable: true,
									onDragEnd: () => {
										if (hoveredCol !== null) {
											const newOrder = [...order];
											const val = newOrder[i];

											newOrder.splice(i, 1);
											newOrder.splice(hoveredCol, 0, val);

											onOrderChange(newOrder);
											setHoveredCol(null);
										}
									},
									onClick: () =>
										onVisibilityChange({
											...visibility,
											[column]: visibility[column] === false,
										}),
								};
							}

							dragProps.onDragEnter = () => {
								setHoveredCol(isModifiable ? i : null);
							};

							return (
								<div key={column} draggable={isModifiable} {...dragProps}>
									<DraggableListItem
										selected={i === hoveredCol}
										isModifiable={isModifiable}
									>
										<FormControlLabel
											control={
												isModifiable ? (
													<OffsetCheckbox checked={visibility[column]} />
												) : (
													<LockIconButton
														disabled
														aria-hidden="false"
														role="button"
														aria-label="lock icon"
													>
														<LockIcon
															aria-label="lock icon"
															role="graphics-symbol"
															aria-hidden="true"
														/>
													</LockIconButton>
												)
											}
											onClick={(e) => e.preventDefault()}
											label={columnT(column)}
										/>
										{isModifiable && <DragIcon />}
									</DraggableListItem>
									<Divider />
								</div>
							);
						})}
					</List>
				</Section>
			</DrawerBody>
		</DrawerButton>
	);
}

ColumnCustomizer.propTypes = {
	title: renderablePropType,
	order: PropTypes.array.isRequired,
	onReset: PropTypes.func.isRequired,
	visibility: PropTypes.object.isRequired,
	onOrderChange: PropTypes.func.isRequired,
	onVisibilityChange: PropTypes.func.isRequired,
	columnConfig: PropTypes.object.isRequired,
	ButtonProps: PropTypes.object,
	dataTestId: PropTypes.string,
};

export default ColumnCustomizer;
